import {Injectable} from '@angular/core';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {AuthActions, AuthApiActions, SigninPageActions} from '../actions';
import {catchError, exhaustMap, switchMap, tap, withLatestFrom} from 'rxjs/operators';
import {AuthenticationService} from '../../../auth/services/authentication.service';
import {of, EMPTY} from 'rxjs';
import {Router} from '@angular/router';
import {StorageService} from '../../../core/storage/storage.service';
import {TranslateService} from '../../../core/translate/translate.service';
import { CookieService } from 'ngx-cookie-service';
import * as Reducers from '../../core/reducers';
import { Store } from '@ngrx/store';
import { TagEnum } from '../../../settings/models/tag.enum';
import {JwtService} from '../../../core/services/jwt.service';
import {User} from '../../../auth/model/user';
import * as SettingsActions from '../../core/actions/settings.actions';
import { UrlHelper } from '../../../shared/helpers/url.helper';
import { MapFacade } from '../../../main/components/map/services/map.facade';

@Injectable()
export class AuthEffects {

  signin$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SigninPageActions.signin),
      switchMap(action =>
        this._authenticationService.login(action.credentials.username, action.credentials.password, action.credentials.language)
          .pipe(
            exhaustMap(user => {
              this._storage.set('currentUser', JSON.stringify(user));
              this.prepareDataFromToken(user);
              this._mapFacade.init();
              return of(AuthApiActions.loginSuccess({user: user, url: action['url']}));
            }),
            catchError(error => of(AuthApiActions.loginFailure({ error })))
          )
      )
    )
  );

  autoLogin$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SigninPageActions.autoLogin),
      switchMap(action => {
        if (this._storage.has('currentUser')) {
          const user = this._storage.get('currentUser');
          this.prepareDataFromToken(user);

          this._mapFacade.init();
          return of(AuthApiActions.loginSuccess({user: user, url: action['url']}));
        } else {
          return EMPTY;
        }
      })
    )
  );

  loginSuccess$ = createEffect(() =>
    this._actions$.pipe(
      ofType(AuthApiActions.loginSuccess.type),
      tap((action) => {
        this._storage.set('currentUser', JSON.stringify(action['user']));
        if (document.getElementById('login')) {
          document.getElementById('login').setAttribute('id', 'body');
        }
        this._router.navigate([UrlHelper.getDefaultUrl(action['user'], action['url'])]);

        this._cookieService.delete('accessToken');
      })
    ),
    { dispatch: false }
  );

  loginRedirect$ = createEffect(() =>
    this._actions$.pipe(
      ofType(AuthApiActions.loginRedirect.type),
      tap(action => {
        this._router.navigate(['/signin'], action['url'] ? {queryParams: {returnUrl: action['url']}} : {});
      })
    ),
    { dispatch: false }
  );

  logout$ = createEffect(() =>
    this._actions$.pipe(
      ofType(AuthActions.logout.type),
      switchMap(action =>
        this._authenticationService.logout().pipe(
          exhaustMap(user => of(AuthApiActions.logoutSuccess())),
          catchError(error => of(AuthApiActions.logoutFailure({ error })))
        )
      )
    )
  );

  logoutSuccess$ = createEffect(() =>
      this._actions$.pipe(
        ofType(AuthApiActions.logoutSuccess.type, AuthApiActions.logoutFailure.type),
        withLatestFrom(
          this._store$.select(Reducers.getTag, TagEnum.RedirectUrl.name.toUpperCase())
        ),
        switchMap(([action, redirectUrlTag]) => {
          const currentUser = this._storage.get('currentUser');
          const redirectUrl = redirectUrlTag ? redirectUrlTag.value.replace(/"/g, '') : null;
          this._storage.remove('currentUser');
          this._translateService.use('pl');
          if (currentUser && redirectUrl) {
            window.location.href = redirectUrl;
            return EMPTY;
          }
          return of(AuthApiActions.loginRedirect({url: null}));
        })
      )
  );

  updateToken$ = createEffect(() =>
      this._actions$.pipe(
        ofType(AuthActions.updateToken.type),
        switchMap(action => {
          const user = this._storage.get('currentUser');
          if (user) {
            user.accessToken = action['token'];
            this.prepareDataFromToken(user);
            this._storage.set('currentUser', JSON.stringify(user));
          }
          return of(SettingsActions.decodeFromToken());
        })
      )
  );

  autoLogout$ = createEffect(() =>
      this._actions$.pipe(
        ofType(SigninPageActions.autoLogout.type),
        tap(action => {
          this._storage.remove('currentUser');
        })
      ),
    { dispatch: false }
  );

  setUser$ = createEffect(() =>
      this._actions$.pipe(
        ofType(AuthActions.setUser.type),
        switchMap(action => {
          const user = this._storage.get('currentUser') || {};
          user.accessToken = action['token'];
          user.language = action['language'];
          this.prepareDataFromToken(user);
          this._translateService.use(action['language']);
          return of(AuthApiActions.loginSuccess({ user, url: null }));
        })
      )
  );

  changeUserLanguage$ = createEffect(() =>
      this._actions$.pipe(
        ofType(AuthActions.changeUserLanguage.type),
        tap(action => {
          const user = this._storage.get('currentUser');
          if (user && user.language !== action['language']) {
            user.language = action['language'];
            this._storage.set('currentUser',  JSON.stringify(user));
            this._translateService.use(action['language']);
            // window.location.reload();
            this._router.navigateByUrl('/settings/accountsettings');
          }
        })
      ), {dispatch: false}
  );


  forgotPassword$ = createEffect(() =>
    this._actions$.pipe(
      ofType(AuthActions.forgotPassword.type),
      switchMap((action: any) =>
        this._authenticationService.forgotPassword({userName: action.userName})
          .pipe(
            exhaustMap(data => of(AuthApiActions.forgotPasswordSuccess({data}))),
            catchError(error => of(AuthApiActions.forgotPasswordFailure({ error })))
          )
      )
    )
  );

  resetPassword$ = createEffect(() =>
    this._actions$.pipe(
      ofType(AuthActions.resetPassword.type),
      switchMap((action: any) =>
        this._authenticationService.resetPassword(action.data)
          .pipe(
            exhaustMap(data => of(AuthApiActions.resetPasswordSuccess({data}))),
            catchError(error => of(AuthApiActions.resetPasswordFailure({ error })))
          )
      )
    )
  );
  constructor(private _actions$: Actions,
              private _store$: Store<any>,
              private _authenticationService: AuthenticationService,
              private _router: Router,
              private _storage: StorageService,
              private _translateService: TranslateService,
              private _cookieService: CookieService,
              private _jwtService: JwtService,
              private _mapFacade: MapFacade

  ) { }

  private prepareDataFromToken(user: User) {
    const decodedToken = this._jwtService.decodeToken(user.accessToken);
    user.modules = JSON.parse(decodedToken['teagleVisability']);
    user.privileges = JSON.parse(decodedToken['privileges']);
    user.settings = JSON.parse(decodedToken['settings']);
    user.clientId = Number(JSON.parse(decodedToken['clientId']));
  }
}
